// Fetch wrapper that auto-injects the Bearer token from MSAL. Anything
// querying our API should go through here so token refresh, JSON
// serialisation, and error shape are consistent.
import { getAccessToken } from "../auth/getToken";

export interface ApiOptions extends Omit<RequestInit, "body"> {
  json?: unknown;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function api<T = unknown>(
  path: string,
  opts: ApiOptions = {},
): Promise<T> {
  const token = await getAccessToken();
  const headers = new Headers(opts.headers ?? {});
  headers.set("Authorization", `Bearer ${token}`);

  let body: BodyInit | undefined;
  if (opts.json !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(opts.json);
  }

  const res = await fetch(path, { ...opts, headers, body });

  let parsed: unknown = null;
  const ct = res.headers.get("content-type") ?? "";
  if (ct.includes("application/json")) {
    parsed = await res.json().catch(() => null);
  } else if (ct.startsWith("text/")) {
    parsed = await res.text().catch(() => null);
  }

  if (!res.ok) {
    const message =
      parsed && typeof parsed === "object" && "error" in parsed
        ? String((parsed as { error: unknown }).error)
        : `${res.status} ${res.statusText}`;
    throw new ApiError(res.status, parsed, message);
  }

  return parsed as T;
}

// Authenticated file download. Fetches with Bearer token, then triggers
// a save-as via a synthesised <a download>. Used for Excel/PDF exports.
export async function apiDownload(path: string, fallbackName: string): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(path, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw new ApiError(res.status, null, `Download failed: ${res.status} ${res.statusText}`);
  }
  const blob = await res.blob();
  const disposition = res.headers.get("content-disposition") ?? "";
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  const filename = match?.[1] ?? fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
